/**
 * Card payment form using Stripe Payment Element.
 * Must be rendered inside <Elements options={{ clientSecret }}>.
 */
import React, { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../ui/Button';
import { Loader2 } from 'lucide-react';

interface CardPaymentFormProps {
  onSuccess: () => void;
  onError: (message: string) => void;
}

export function CardPaymentForm({ onSuccess, onError }: CardPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsConfirming(true);
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
        },
      });

      if (error) {
        onError(error.message ?? 'Payment failed');
        return;
      }

      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />
      <Button
        type="submit"
        className="w-full py-3 bg-green-700 hover:bg-green-800 text-white"
        disabled={!stripe || !elements || isConfirming}
      >
        {isConfirming ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2 inline" />
            Processing…
          </>
        ) : (
          'Pay Now'
        )}
      </Button>
    </form>
  );
}
